/*
  <recently-viewed> - see sections/recently-viewed.liquid for the why.

  1. On a product page, remember its handle in localStorage (most recent first).
     localStorage outlives the session, so products from earlier visits on the
     same browser/device are included.
  2. When the row is near the viewport, fetch each remembered product's card
     from the recently-viewed-card render proxy (Section Rendering API),
     build the same slider markup as "You May Also Like", and show the row.
     Products that no longer exist (404: deleted/unpublished) are dropped
     from storage.

  The full slider markup is built before it is inserted, so slider-component,
  card-product and card-media-carousel initialise normally on insertion.
*/
(() => {
  if (customElements.get('recently-viewed')) return;

  const STORAGE_KEY = 'lp_recently_viewed';
  const MAX_STORED = 20;

  const readList = () => {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  };
  const writeList = (list) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
    } catch (e) {
      /* storage blocked (private mode etc.): the row simply stays hidden */
    }
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      const current = this.dataset.current;
      let list = readList();
      if (current) {
        list = [current, ...list.filter((h) => h !== current)];
        writeList(list);
      }

      const limit = parseInt(this.dataset.limit, 10) || 8;
      // Fetch a few spares in case some products have been removed.
      this.handles = list.filter((h) => h !== current).slice(0, limit + 4);
      this.limit = limit;
      if (!this.handles.length) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '800px 0px' }
        );
        observer.observe(this);
      } else {
        this.load();
      }
    }

    async load() {
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const responses = await Promise.all(
        this.handles.map((handle) =>
          fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`)
            .then((res) => (res.ok ? res.text() : res.status === 404 ? 404 : null))
            .catch(() => null)
        )
      );

      const cards = [];
      const gone = [];
      responses.forEach((html, i) => {
        if (html === 404) {
          gone.push(this.handles[i]);
          return;
        }
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('.card-wrapper');
        if (card) cards.push(card.outerHTML);
      });

      if (gone.length) writeList(readList().filter((h) => !gone.includes(h)));
      const shown = cards.slice(0, this.limit);
      if (!shown.length) return;

      this.render(shown);
      this.hidden = false;
    }

    render(cards) {
      const id = this.dataset.sectionId;
      const count = cards.length;
      const colsDesktop = parseInt(this.dataset.colsDesktop, 10) || 4;
      const colsMobile = parseInt(this.dataset.colsMobile, 10) || 2;
      // Always use the slider layout (adam, 28 Sep), even with fewer products
      // than a row: card sizes then match "You May Also Like" (which is always
      // a slider) instead of stretching to fill a plain grid.
      const mobileSlider = true;
      const desktopSlider = true;
      const anySlider = true;

      const heading = this.querySelector('template[data-rv-heading]').innerHTML;
      const caret = this.querySelector('template[data-rv-caret]').innerHTML;
      const labels = this.querySelector('template[data-rv-labels]').dataset;

      const sliderClasses = [
        'slider-mobile-gutter',
        'slider-component-full-width',
        mobileSlider ? '' : 'page-width',
        desktopSlider ? 'slider-component-desktop' : '',
      ].filter(Boolean).join(' ');

      const titleClasses = [
        'collection__title title-wrapper title-wrapper--no-top-margin page-width',
        mobileSlider ? 'title-wrapper--self-padded-tablet-down' : '',
        desktopSlider ? 'collection__title--desktop-slider' : '',
      ].filter(Boolean).join(' ');

      const listClasses = [
        `grid product-grid contains-card contains-card--product grid--${colsDesktop}-col-desktop grid--${colsMobile}-col-tablet-down`,
        anySlider ? 'slider' : '',
        desktopSlider ? 'slider--desktop' : '',
        mobileSlider ? 'slider--tablet grid--peek' : '',
      ].filter(Boolean).join(' ');

      const buttons = anySlider
        ? `<div class="slider-buttons">
            <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="${labels.prev}" aria-controls="Slider-${id}">${caret}</button>
            <div class="slider-counter caption">
              <span class="slider-counter--current">1</span>
              <span aria-hidden="true"> / </span>
              <span class="visually-hidden">${labels.of}</span>
              <span class="slider-counter--total">${count}</span>
            </div>
            <button type="button" class="slider-button slider-button--next" name="next" aria-label="${labels.next}" aria-controls="Slider-${id}">${caret}</button>
          </div>`
        : '';

      const items = cards
        .map(
          (card, i) =>
            `<li id="Slide-${id}-${i + 1}" class="grid__item${anySlider ? ' slider__slide' : ''}">${card}</li>`
        )
        .join('');

      this.innerHTML = `<slider-component class="${sliderClasses}">
          <div class="${titleClasses}">
            <h2 class="related-products__heading inline-richtext h2">${heading}</h2>
            ${buttons}
          </div>
          <ul id="Slider-${id}" class="${listClasses}" role="list" aria-label="${labels.name}">${items}</ul>
        </slider-component>`;
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
})();
